/**
 * 座位、按钮与盲注移动。见 docs/ARCHITECTURE.md §4.12 / §4.13 / §4.15。
 *
 * 采用现金局标准的**死按钮**（Robert's Rules of Poker v11 §4-2(b)、WSOP 2025 Live Action §154、
 * TDA Rule 32）：
 *   - 大盲：上一手大盲之后顺时针第一个有资格的座位。谁也不能被跳过交大盲。
 *   - 小盲：放在**上一手大盲的座位**上；那个人本手不发牌 → 死小盲（这手没有小盲）。
 *   - 按钮：放在**上一手小盲的位置**上；那个人本手不发牌 → 死按钮。
 * 于是每一圈每人恰好交一次大盲、一次小盲、拿一次按钮；死小盲 / 死按钮只在「该出的人不在」时出现。
 *
 * ★ 以前小盲取「大盲的物理前一座」、按钮取「小盲的物理前一座」。桌子没坐满时（6 座坐 3 人），
 *   从来没人坐过的空座也被当成死小盲 / 死按钮：线上 252 手 3 人以上的牌里 16% 没有小盲，
 *   坐在空座后面的人永远不用交小盲。
 *
 * 入局（§4-4 ~ §4-12）：新玩家、欠盲的老玩家属于「入局中」。他们只在两种情况下发牌：
 *   (a) 本手大盲轮到他（欠盲一笔勾销）；
 *   (b) 选择了补盲，且不在「按钮 → 大盲」这一段（盲注刚经过的地方，§4-7）。
 *   补盲：新玩家 / 欠大盲 → 交一个**活**大盲（算本人下注，轮到他照样有 option）；
 *        欠小盲 → 另交一个**死**小盲（进池中间，不算本人下注）。
 *
 * 铁律 #1：本模块是纯函数。指针（上一手的大盲座位、小盲位置）由调用方持有并传入，引擎不维护。
 * 这同时满足红线 #25——手牌被 void 时调用方原样保留指针再调一次，得到完全相同的分配；
 * 作废的手牌在按钮序列上不存在。
 */
import { SEAT_LIMITS } from '../config/roomConfig.js'
import type { AccountId, Chips, SeatIndex } from '../ids.js'
import type { EngineResult, Position } from './types.js'
import { InvariantError, engineErr, engineOk } from './types.js'

/** 入局中的座位（新玩家 / 欠盲的老玩家）。正常在局的座位为 null。 */
export interface SeatEntry {
  /** 本桌还没被发过牌的新玩家（不算欠盲） */
  readonly newPlayer: boolean
  /** 盲注经过时不在（坐出 / 输光 / 断线被坐出）而错过的大盲 */
  readonly owesBigBlind: boolean
  /** 同上，错过的小盲 */
  readonly owesSmallBlind: boolean
  /** 本人选择补盲入局；否则等大盲 */
  readonly post: boolean
  /**
   * 房间设置「新玩家免费入局」（newPlayerPolicy = dealInImmediately）：新玩家补盲不交钱，
   * 但照样受「按钮 → 大盲」这一段的限制（否则会白拿按钮位）。只免新玩家那份，欠的盲照补。
   */
  readonly freeForNewPlayer: boolean
}

export interface TableSeatSnapshot {
  readonly seat: SeatIndex
  /** null = 空座。空座仍占据物理位置，死按钮 / 死小盲可能落在它上面。 */
  readonly accountId: AccountId | null
  readonly stack: Chips
  readonly sittingOut: boolean
  readonly entry: SeatEntry | null
}

export interface BlindAssignment {
  readonly buttonSeat: SeatIndex
  readonly smallBlindSeat: SeatIndex | null // null = dead small blind
  readonly bigBlindSeat: SeatIndex
  /**
   * 本手的小盲**位置**（即使是死小盲也有）。调用方在手牌 complete 时把它存成下一手的
   * `prevSmallBlindSeat`。单挑时 = 按钮兼小盲的座位。
   */
  readonly smallBlindPosition: SeatIndex
  readonly deadButton: boolean // 按钮落在空座（或未被发牌的座位）
  readonly isHeadsUp: boolean
  readonly dealtSeats: readonly SeatIndex[] // 发牌顺序，dealtSeats[0] 为第一个收牌者
  /** 补一个活大盲入局的座位（按发牌顺序） */
  readonly livePostSeats: readonly SeatIndex[]
  /** 补一个死小盲的座位（按发牌顺序） */
  readonly deadSmallBlindSeats: readonly SeatIndex[]
}

export interface AssignSeatsOptions {
  readonly maxSeats: number
  /**
   * 上一手 complete 的小盲**位置**（`BlindAssignment.smallBlindPosition`）。
   * null = 没有记录（房间第一手、老数据）：按钮改取「小盲位置逆时针方向最近的在局座位」。
   */
  readonly prevSmallBlindSeat: SeatIndex | null
  /**
   * 按一张新桌子开：忽略入局标记与封锁段，所有有资格的人都发牌，按钮 / 小盲按在座的人排。
   * 两种场合：房间第一手；以及「按规矩一个都发不出来」的僵局（调用方重试时传）。
   */
  readonly freshTable?: boolean
}

/** 从 `start`（含）顺时针找第一个属于 `member` 的座位；找不到返回 null。 */
function firstClockwise(
  start: number,
  maxSeats: number,
  member: readonly boolean[],
): SeatIndex | null {
  for (let k = 0; k < maxSeats; k++) {
    const s = (((start + k) % maxSeats) + maxSeats) % maxSeats
    if (member[s] === true) return s
  }
  return null
}

/** 从 `start`（含）逆时针找第一个属于 `member` 的座位；找不到返回 null。 */
function firstCounterClockwise(
  start: number,
  maxSeats: number,
  member: readonly boolean[],
): SeatIndex | null {
  for (let k = 0; k < maxSeats; k++) {
    const s = (((start - k) % maxSeats) + maxSeats) % maxSeats
    if (member[s] === true) return s
  }
  return null
}

/** 从 `start`（含）顺时针列出所有属于 `member` 的座位。 */
function clockwiseFrom(
  start: number,
  maxSeats: number,
  member: readonly boolean[],
): SeatIndex[] {
  const out: SeatIndex[] = []
  for (let k = 0; k < maxSeats; k++) {
    const s = (start + k) % maxSeats
    if (member[s] === true) out.push(s)
  }
  return out
}

/** `x` 是否严格位于从 `from` 顺时针走到 `to` 的开区间里（from === to 时为整圈去掉该点） */
export function inOpenArc(x: number, from: number, to: number, maxSeats: number): boolean {
  if (x === from || x === to) return false
  const dx = (x - from + maxSeats) % maxSeats
  const dt = (to - from + maxSeats) % maxSeats
  return dt === 0 ? true : dx < dt
}

/** 有资格交盲：有人、有筹码、没 sitOut。入局中的人**在此集合内**（大盲指针要能走到他）。 */
function isBlindEligible(s: TableSeatSnapshot): boolean {
  return s.accountId !== null && s.stack > 0 && !s.sittingOut
}

/**
 * 计算本手的按钮、盲注、发牌顺序与补盲。
 *
 * `prevBigBlindSeat` / `opts.prevSmallBlindSeat` 是上一手 **complete** 的大盲座位与小盲位置
 * （红线 #25：voided 手不推进它们）；`prevBigBlindSeat === null` = 本房间第一手。
 */
export function assignSeats(
  seats: readonly TableSeatSnapshot[],
  prevBigBlindSeat: SeatIndex | null,
  opts: AssignSeatsOptions,
): EngineResult<BlindAssignment> {
  const maxSeats = opts.maxSeats
  if (
    !Number.isInteger(maxSeats) ||
    maxSeats < SEAT_LIMITS.MIN ||
    maxSeats > SEAT_LIMITS.MAX
  ) {
    return engineErr('INVALID_CONFIG', `maxSeats out of range: ${String(maxSeats)}`)
  }

  // —— 输入校验（§4.14 startHand 校验清单里属于座位的那几行）——
  const bySeat: (TableSeatSnapshot | undefined)[] = new Array<TableSeatSnapshot | undefined>(
    maxSeats,
  ).fill(undefined)
  const seenAccounts = new Set<string>()
  for (const s of seats) {
    if (!Number.isInteger(s.seat) || s.seat < 0 || s.seat >= maxSeats) {
      return engineErr('INVALID_CONFIG', `seat out of range: ${String(s.seat)}`)
    }
    if (bySeat[s.seat] !== undefined) {
      return engineErr('INVALID_CONFIG', `duplicate seat: ${s.seat}`, s.seat)
    }
    // 红线 #17：筹码全是整数
    if (!Number.isInteger(s.stack) || s.stack < 0) {
      return engineErr('INVALID_CONFIG', `invalid stack at seat ${s.seat}`, s.seat)
    }
    if (s.accountId !== null) {
      if (seenAccounts.has(s.accountId)) {
        return engineErr('INVALID_CONFIG', `duplicate accountId at seat ${s.seat}`, s.seat)
      }
      seenAccounts.add(s.accountId)
    }
    bySeat[s.seat] = s
  }
  for (const [name, v] of [
    ['prevBigBlindSeat', prevBigBlindSeat],
    ['prevSmallBlindSeat', opts.prevSmallBlindSeat],
  ] as const) {
    if (v !== null && (!Number.isInteger(v) || v < 0 || v >= maxSeats)) {
      return engineErr('INVALID_CONFIG', `${name} out of range: ${String(v)}`)
    }
  }

  const eligible = new Array<boolean>(maxSeats).fill(false)
  let eligibleCount = 0
  let firstEligible: SeatIndex | null = null
  for (let i = 0; i < maxSeats; i++) {
    const s = bySeat[i]
    if (s !== undefined && isBlindEligible(s)) {
      eligible[i] = true
      eligibleCount++
      if (firstEligible === null) firstEligible = i
    }
  }
  if (eligibleCount < 2 || firstEligible === null) {
    return engineErr('NOT_ENOUGH_PLAYERS', `blind-eligible seats: ${eligibleCount}`)
  }

  // 房间第一手 / 僵局重开：按一张新桌子处理
  const fresh = opts.freshTable === true || prevBigBlindSeat === null
  const entryOf = (i: number): SeatEntry | null => (fresh ? null : (bySeat[i]?.entry ?? null))

  // —— 1) 推进大盲指针 ——
  // eligibleCount >= 2 保证从 prev+1 起一定先撞上别人，故 bbSeat !== prevBigBlindSeat，
  // 即「没有人连续两手交大盲」是由构造保证的，不靠事后断言。
  let bbSeat: SeatIndex
  if (prevBigBlindSeat === null) {
    bbSeat = firstEligible
  } else {
    const found = firstClockwise(prevBigBlindSeat + 1, maxSeats, eligible)
    if (found === null) throw new InvariantError('assignSeats: no eligible seat for big blind')
    bbSeat = found
  }

  // 「正常在局」的座位：有资格、且不在入局中（大盲位上的入局者本手照常入局，也算）
  const regular = eligible.map((e, i) => e && (i === bbSeat || entryOf(i) === null))
  const regularExceptBb = regular.map((r, i) => r && i !== bbSeat)

  // —— 2) 小盲位置与按钮位置（死按钮：取上一手的指针）——
  let sbPos: SeatIndex
  let btnPos: SeatIndex
  let wrapped = false
  if (fresh) {
    // 新桌：按在座的人排，大盲逆时针依次是小盲、按钮
    sbPos = firstCounterClockwise(bbSeat - 1, maxSeats, regularExceptBb) ?? bbSeat
    const beforeSb = regularExceptBb.map((r, i) => r && i !== sbPos)
    btnPos = firstCounterClockwise(sbPos - 1, maxSeats, beforeSb) ?? sbPos
  } else {
    sbPos = prevBigBlindSeat
    const prevSb = opts.prevSmallBlindSeat
    /**
     * 指针「绕回」：按钮位置不在小盲位置之前（等于小盲 / 大盲，或落在小盲与大盲之间）。
     * 单挑变多人时每次都会走到这里（单挑时上一手的小盲就是本手大盲），另外就是只剩两个
     * 有资格的人时。此时按钮改取「小盲位置逆时针最近的在局座位」，并且本手不放任何入局者
     * 补盲进来 —— 否则补盲的新人会直接拿到按钮（设计评审的模拟里约 2000 / 2700 次）。
     *
     * 另一种不一致：按钮位置与小盲位置之间夹着正常在局的人（比如输光时已申请买分、
     * 房间没给他记欠盲）。正常在局的人不能被挡在外面，所以同样挪按钮，让他当按钮 / 夹不进去。
     */
    const consistent =
      prevSb !== null && prevSb !== sbPos && prevSb !== bbSeat && !inOpenArc(prevSb, sbPos, bbSeat, maxSeats)
    const regularInside =
      consistent && regularExceptBb.some((r, i) => r && inOpenArc(i, prevSb, sbPos, maxSeats))
    if (!consistent || regularInside) {
      wrapped = prevSb !== null
      const candidates = regularExceptBb.map((r, i) => r && i !== sbPos)
      btnPos = firstCounterClockwise(sbPos - 1, maxSeats, candidates) ?? sbPos
    } else {
      btnPos = prevSb
    }
  }

  // —— 3) 谁发牌 ——
  // 「按钮 → 大盲」这一段（按钮位置、小盲位置以及两者之间）是盲注刚经过的地方（§4-7）：
  // 入局中的人不能在这里入局。正常在局的人不受影响 —— 上面选按钮时已保证他们不会夹在中间。
  const blocked = (i: number): boolean => {
    if (fresh || i === bbSeat || entryOf(i) === null) return false
    if (wrapped) return true
    return i === btnPos || i === sbPos || inOpenArc(i, btnPos, sbPos, maxSeats)
  }
  const dealtIn = new Array<boolean>(maxSeats).fill(false)
  let dealtInCount = 0
  for (let i = 0; i < maxSeats; i++) {
    if (!eligible[i] || blocked(i)) continue
    const entry = entryOf(i)
    // 入局中的人：轮到大盲即入局；否则要选了补盲才入局
    if (entry !== null && i !== bbSeat && !entry.post) continue
    dealtIn[i] = true
    dealtInCount++
  }
  // 单挑不接受补盲者：两人桌上补盲的人会同时是按钮兼小盲，规则上无从谈起
  if (dealtInCount === 2) {
    for (let i = 0; i < maxSeats; i++) {
      if (dealtIn[i] && i !== bbSeat && entryOf(i) !== null) {
        dealtIn[i] = false
        dealtInCount--
      }
    }
  }
  if (dealtInCount < 2) {
    return engineErr('NOT_ENOUGH_PLAYERS', `dealt-in seats: ${dealtInCount}`)
  }
  if (dealtIn[bbSeat] !== true) {
    throw new InvariantError('assignSeats: big blind seat is not dealt in')
  }
  const isHeadsUp = dealtInCount === 2

  // —— 4) 小盲与按钮 ——
  let sbSeat: SeatIndex | null
  let buttonSeat: SeatIndex
  let deadButton: boolean
  let smallBlindPosition: SeatIndex
  if (isHeadsUp) {
    // 单挑：按钮即小盲（翻前按钮先行动、翻后按钮后行动，与 3+ 人完全相反）。
    // 单挑永远不可能出现 dead blind——两人都拿牌，盲注必然落在真人身上。
    const other = firstClockwise(bbSeat + 1, maxSeats, dealtIn)
    if (other === null || other === bbSeat) {
      throw new InvariantError('assignSeats: heads-up opponent not found')
    }
    sbSeat = other
    buttonSeat = other
    deadButton = false
    smallBlindPosition = other
  } else {
    if (sbPos === bbSeat || btnPos === bbSeat) {
      throw new InvariantError(`assignSeats: bad positions sb=${sbPos} btn=${btnPos} bb=${bbSeat}`)
    }
    // 小盲位置上的人本手没发牌 → 死小盲：这笔小盲没人交，本手直接少收（不向后顺延）
    sbSeat = dealtIn[sbPos] === true ? sbPos : null
    buttonSeat = btnPos
    deadButton = dealtIn[btnPos] !== true
    smallBlindPosition = sbPos
  }

  // —— 5) 硬断言：违反即引擎自身有 bug（铁律 #4）——
  if (bbSeat === sbSeat) {
    throw new InvariantError(`assignSeats: big blind === small blind at seat ${bbSeat}`)
  }
  if (isHeadsUp ? buttonSeat !== sbSeat : buttonSeat === bbSeat) {
    throw new InvariantError(
      `assignSeats: bad button ${buttonSeat} (bb=${bbSeat}, sb=${String(sbSeat)}, hu=${String(isHeadsUp)})`,
    )
  }
  if (!isHeadsUp && buttonSeat !== smallBlindPosition) {
    for (let i = 0; i < maxSeats; i++) {
      if (dealtIn[i] && inOpenArc(i, buttonSeat, smallBlindPosition, maxSeats)) {
        throw new InvariantError(`assignSeats: seat ${i} dealt between button ${buttonSeat} and small blind ${smallBlindPosition}`)
      }
    }
  }

  // §4.15：底牌两轮，每轮按 dealtSeats 顺序；dealtSeats[0] = 小盲，dead SB 时 = 大盲。
  const dealtSeats = clockwiseFrom(sbSeat ?? bbSeat, maxSeats, dealtIn)
  if (dealtSeats.length !== dealtInCount) {
    throw new InvariantError('assignSeats: dealtSeats size mismatch')
  }

  // 补盲：本手的大盲 / 小盲位上的人不重复补（大盲位上的入局者欠盲一笔勾销）
  const livePostSeats: SeatIndex[] = []
  const deadSmallBlindSeats: SeatIndex[] = []
  for (const s of dealtSeats) {
    if (s === bbSeat || s === sbSeat) continue
    const entry = entryOf(s)
    if (entry === null) continue
    if (entry.owesBigBlind || (entry.newPlayer && !entry.freeForNewPlayer)) livePostSeats.push(s)
    if (entry.owesSmallBlind) deadSmallBlindSeats.push(s)
  }

  return engineOk({
    buttonSeat,
    smallBlindSeat: sbSeat,
    bigBlindSeat: bbSeat,
    smallBlindPosition,
    deadButton,
    isHeadsUp,
    dealtSeats,
    livePostSeats,
    deadSmallBlindSeats,
  })
}

/**
 * 位置标签池（§4.12）。BTN/SB/BB 三个固定角色单独处理，这里只放前中位：
 * 人数不足时**从 CO 端向前裁剪**（先砍 CO，再砍 HJ……），所以 n 人桌取前 n-3 个。
 */
/**
 * 早期位标签，按「离按钮由近到远」排列。
 *
 * 必须从 CO 端向前对齐，不能从 UTG 端向后数：CO（cutoff）的定义就是**按钮右手第一位**，
 * HJ（hijack）是 CO 右手第一位，与牌桌人数无关。若从 UTG 端数，8 人桌的按钮右手位
 * 会被叫成 HJ、6 人桌干脆没有 CO —— 而标准 6-max 的叫法正是 UTG / HJ / CO。
 *
 * 唯一例外：最早行动的座位恒为 UTG（under the gun 就是"第一个被迫说话的人"）。
 */
const LATE_TO_EARLY = ['CO', 'HJ', 'MP1', 'MP', 'UTG1'] as const

/** 给 `count` 个早期位按行动顺序命名：UTG 打头，其余从 CO 端向前对齐。 */
function earlyLabels(count: number): Position[] {
  if (count <= 0) return []
  if (count - 1 > LATE_TO_EARLY.length) {
    throw new InvariantError(`earlyLabels: 早期位 ${count} 超出标签池 ${LATE_TO_EARLY.length + 1}`)
  }
  return ['UTG', ...LATE_TO_EARLY.slice(0, count - 1).reverse()]
}

const THREE_HANDED = ['BTN', 'SB', 'BB'] as const

/**
 * `seatOrder` = 从**翻前第一个行动者**起的下标（不是座位号，也不是发牌顺序下标）。
 * 只用于位置统计，行动权推进一律走 `nextToAct`（红线 #22）。
 *
 * 三种人数的角色序完全不同：
 *   n=2  BTN_SB, BB           （单挑按钮兼小盲，翻前先行动）
 *   n=3  BTN, SB, BB          （3 人时按钮就是 UTG）
 *   n>=4 UTG…CO, BTN, SB, BB
 */
export function positionOf(seatOrder: number, playerCount: number): Position {
  if (!Number.isInteger(seatOrder) || !Number.isInteger(playerCount)) {
    throw new InvariantError(
      `positionOf: non-integer input (${String(seatOrder)}, ${String(playerCount)})`,
    )
  }
  if (playerCount < SEAT_LIMITS.MIN || playerCount > SEAT_LIMITS.MAX) {
    throw new InvariantError(`positionOf: playerCount out of range: ${playerCount}`)
  }
  if (seatOrder < 0 || seatOrder >= playerCount) {
    throw new InvariantError(`positionOf: seatOrder ${seatOrder} out of 0..${playerCount - 1}`)
  }
  if (playerCount === 2) return seatOrder === 0 ? 'BTN_SB' : 'BB'
  if (playerCount === 3) return THREE_HANDED[seatOrder]!
  if (seatOrder === playerCount - 1) return 'BB'
  if (seatOrder === playerCount - 2) return 'SB'
  if (seatOrder === playerCount - 3) return 'BTN'
  return earlyLabels(playerCount - 3)[seatOrder]!
}

/**
 * 按**实际盲注座位**给本手每个玩家打位置标签。
 *
 * 为什么不能用 `positionOf(下标, 人数)`：那个函数从下标反推角色，隐含假设
 * 「倒数第二个一定是小盲、倒数第三个一定是按钮」。dead small blind 的手牌里
 * 根本没有小盲位，dead button 时按钮座位空着、压根不在 `preflopOrder` 里——
 * 两种情况下整排标签都会错位一格，与同一个 handStarted 事件里的
 * buttonSeat / sbSeat 自相矛盾。而这两种情况正是 BB 指针推进的常见副作用。
 *
 * 仅用于展示与统计；行动权推进一律走 `nextToAct`（红线 #22）。
 *
 * @param preflopOrder 从翻前第一个行动者（大盲左手）起、顺时针排列的**被发牌**座位
 */
export function positionsForHand(
  a: Pick<BlindAssignment, 'buttonSeat' | 'smallBlindSeat' | 'bigBlindSeat'>,
  preflopOrder: readonly SeatIndex[],
): ReadonlyMap<SeatIndex, Position> {
  const out = new Map<SeatIndex, Position>()

  // 单挑：按钮兼小盲，翻前先行动
  if (preflopOrder.length === 2) {
    for (const s of preflopOrder) out.set(s, s === a.buttonSeat ? 'BTN_SB' : 'BB')
    return out
  }

  // 先认领三个具名位置，剩下的按行动顺序排早期位
  const early: SeatIndex[] = []
  for (const s of preflopOrder) {
    if (s === a.bigBlindSeat) out.set(s, 'BB')
    else if (a.smallBlindSeat !== null && s === a.smallBlindSeat) out.set(s, 'SB')
    else if (s === a.buttonSeat) out.set(s, 'BTN')
    else early.push(s)
  }

  // 标签池够用的证明：每缺一个盲注/按钮位，必然对应一个空座；空座又让 preflopOrder
  // 少一个人。两边同时减，早期位数恒 <= SEAT_LIMITS.MAX - 3。
  const labels = earlyLabels(early.length)
  early.forEach((s, i) => out.set(s, labels[i]!))
  return out
}
